import React, {useEffect, useState} from 'react';
import './App.scss';
import EventInternalNav from "./components/EventInternalNav";
import EventHeader from "./components/event-header/EventHeader";
import Speakers from './components/speakers/Speakers';
import Sessions from "./components/sessions/Sessions";
import {handleNavScroll} from "./utils/navigation";
import useEventData from "./hooks/useEventData";
import Sponsors from "./components/sponsors/Sponsors";
import SessionTrackSelect from "./components/sessions/SessionTrackSelect";
import EventRegistrationButton from "./components/EventRegistrationButton";
import {HtmlComponent} from "./components/static/HtmlComponent";

function MassBioEvent() {
    const [eventNavLinks, setNavLinks] = useState({});
    const [activeTab, setActiveTab] = useState('agenda')
    const [sessionTracks, setSessionTracks] = useState(null);
    const [sessionTrackFilter, setSessionTrackFilter] = useState('');

    const event = useEventData();
    const {
        additionalInformation,
        eventDescription,
        speakers,
        sponsorCount,
        sponsors,
        sessions,
		videoEmbed,
    } = event.data;

    const [filteredSessions, setFilteredSessions] = useState(sessions)

	// Request event data on component mount.
    useEffect(() => {
        event.request();
        // Run once on mount. `event` is a fresh object every render, so it cannot be a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Since the Add to Calendar button isn't available on page load, trigger an Add Event JS refresh once
    // event data is loaded.
    useEffect(() => {
        if (event.loaded) {
			// Add to Calendar
			if (window.addeventatc) {
				window.addeventatc.refresh();
			}

			// AddToAny
			if (window.a2a) {
				window.a2a.init_all();
			}
        }
    }, [event.loaded])

    // Populate Session Tracks data and set the initial value of Filtered Sessions.
    useEffect(() => {
        const sessionsArray = Object.values(sessions);
        const tracks = sessionsArray.map(session => session.track).filter(track => track !== null)
        const uniqueTracks = new Set(Array.from(tracks));
        setSessionTracks(uniqueTracks)
        setFilteredSessions(sessions)
    }, [sessions])

    // Handle filtering event sessions when the session track filter changes.
    useEffect(() => {
        const sessionsArray = Object.values(sessions);

        if (sessionTrackFilter === '') {
            setFilteredSessions(sessionsArray)
            return;
        }

        const filteredSessions = sessionsArray.filter(session => {
            return session.track === sessionTrackFilter;
        })
        setFilteredSessions(filteredSessions)
        // Re-filter only when the user changes the track filter; resetting on session load is
        // handled by the effect above, so `sessions` is intentionally not a dependency here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionTrackFilter])

	// Scroll to the active tab when it changes.
    useEffect(() => {
        const targetSection = document.getElementById(activeTab)
        if (targetSection) {
            targetSection.scrollIntoView({
                behavior: 'smooth',
            })
        }
    }, [activeTab])

	// Add scroll event listener to handle navigation scroll.
    useEffect(() => {
        if (event.loaded) {
            window.addEventListener('scroll', handleNavScroll)
        }
    }, [event.loaded])

    // Handle dynamically populating the sidebar navigation by only showing items with content.
    useEffect(() => {
		const {
            additionalInformation,
            eventDescription,
            sessions,
            speakers,
            sponsorCount,
			videoEmbed,
        } = event.data;

        const navLinks = {};

        if (eventDescription && eventDescription.length > 0) {
            navLinks.event_description = 'Event Detail';
        }

		if (videoEmbed) {
			navLinks.video = 'Video';
		}

        if (sessions && sessions.length > 0) {
            navLinks.agenda = 'Agenda';
        }

        if (speakers && speakers.length > 0) {
            navLinks.speakers = 'Speakers';
        }

        if (sponsorCount > 0) {
            navLinks.sponsors = 'Sponsors';
        }

        if (additionalInformation && additionalInformation.length > 0) {
            navLinks.additional_information = 'Additional Information';
        }

        setNavLinks(navLinks)
    }, [event.data])

    if (event.loaded && event.error) {
        return (
            <>
                <p>There was an error: {event.error}</p>
            </>
        )
    }

    return (
        <>
            <EventHeader eventLoaded={event.loaded} event={event.data}/>

            {
                event.loaded && !event.error &&
                <section id="event">

                    <div id="event_nav">
                        <EventRegistrationButton event={event.data} extraClasses={['register-link']}/>
                        <EventInternalNav activeTab={activeTab} tabs={eventNavLinks} clickHandler={setActiveTab}/>
                    </div>

					<section id="event_container">

						{
							eventDescription && eventDescription.length > 0 &&
							<section id="event_description"
									 dangerouslySetInnerHTML={{__html: event.data.eventDescription}}/>
						}

						{
							videoEmbed &&
							<section id="video" className={activeTab === 'video' ? 'active' : ''}>
								<h2>Video</h2>

								<div className="event-video embed-container">
									<HtmlComponent template={videoEmbed}/>
								</div>
							</section>
						}

						{
							sessions.length > 0 &&
							<section id="agenda" className={activeTab === 'agenda' ? 'active' : ''}>
								<h2>Agenda</h2>

								{
									sessionTracks.size > 0 &&
									<SessionTrackSelect sessionTrackFilter={sessionTrackFilter}
														sessionTracks={sessionTracks}
														changeHandler={setSessionTrackFilter}/>
								}

								<Sessions eventLoading={event.loading} sessions={filteredSessions} speakers={speakers}
										  trackFilter={sessionTrackFilter}/>

							</section>
						}

						{
							speakers.length > 0 &&
							<section id="speakers" className={`${activeTab === 'speakers' ? 'active' : ''}`}>
								<h2>Speakers</h2>
								<Speakers speakers={speakers} sessions={sessions}/>
							</section>
						}

						{
							sponsorCount > 0 &&
							<Sponsors sponsors={sponsors} isActive={activeTab === 'sponsors'}/>
						}

						{
							additionalInformation && additionalInformation.length > 0 &&
							<section id="additional_information">
								<h2>Additional Information</h2>
								<div dangerouslySetInnerHTML={{__html: additionalInformation}}/>
							</section>
						}

					</section>
				</section>
            }
        </>
    );
}

export default MassBioEvent;
